import {
  IRxTransport,
  ITxTransport,
} from "lib/deviceDrivers/sessionFramework/device/types";
import {
  DriverEventType,
  flushDriverEffects,
  type DriverEffect,
  type DriverNotifier,
} from "lib/deviceDrivers/sessionFramework/adapter/driverEffects";
import { toastNotifier } from "lib/deviceDrivers/sessionFramework/adapter/toastNotifier";

/**
 * Generic session base that bridges a device protocol handle to the Redux action pipeline.
 *
 * Two separate effect queues enforce the ordering invariant structurally:
 * domain effects (Redux state updates) are always drained before send effects
 * (outgoing protocol writes) so downstream state is consistent before the next command goes out.
 */
export class DriverSession<
  TDeviceHandle extends { destroy(): void },
  TAction,
  TEvent,
> {
  protected readonly device: TDeviceHandle;

  private readonly domainEffects: DriverEffect<TAction>[] = [];
  private readonly sendEffects: DriverEffect<TAction>[] = [];
  private receiveHandler: (payload: string) => void = () => {};

  constructor(
    protected readonly deviceId: string,
    createDevice: (
      tx: ITxTransport,
      rx: IRxTransport,
      onEvent: (event: TEvent) => void,
    ) => TDeviceHandle,
    createSendAction: (deviceId: string, payload: string) => TAction,
    mapEventToEffects: (
      deviceId: string,
      event: TEvent,
    ) => DriverEffect<TAction>[],
    protected readonly notifier: DriverNotifier = toastNotifier,
  ) {
    const sessionDeviceId = this.deviceId;

    const tx: ITxTransport = {
      send: payload => {
        this.sendEffects.push({
          type: DriverEventType.ACTION,
          action: createSendAction(sessionDeviceId, payload),
        });
        return Promise.resolve();
      },
    };

    const rx: IRxTransport = {
      onReceive: handler => {
        this.receiveHandler = handler;
        return () => {
          if (this.receiveHandler === handler) {
            this.receiveHandler = () => {};
          }
        };
      },
    };

    this.device = createDevice(tx, rx, event => {
      this.domainEffects.push(...mapEventToEffects(sessionDeviceId, event));
    });
  }

  receive(payload: string): TAction[] | undefined {
    this.receiveHandler(payload);
    return this.drainEffects();
  }

  tick(): TAction[] | undefined {
    return this.drainEffects();
  }

  destroy(): void {
    this.device.destroy();
    this.domainEffects.length = 0;
    this.sendEffects.length = 0;
  }

  protected drainEffects(): TAction[] | undefined {
    return flushDriverEffects(
      [...this.domainEffects.splice(0), ...this.sendEffects.splice(0)],
      this.notifier,
    );
  }
}
